import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

export function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="field">
            <label>{label}</label>
            {children}
        </div>
    );
}

export function TextField(props: {
    label: string; value: string; onChange: (v: string) => void;
    placeholder?: string; multiline?: boolean; disabled?: boolean;
}) {
    return (
        <Field label={props.label}>
            {props.multiline ? (
                <textarea
                    value={props.value}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onChange={(e) => props.onChange(e.target.value)}
                />
            ) : (
                <input
                    value={props.value}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    onChange={(e) => props.onChange(e.target.value)}
                />
            )}
        </Field>
    );
}

export function NumberField(props: {
    label: string; value: number; onChange: (v: number) => void; step?: number; narrow?: boolean;
}) {
    return (
        <Field label={props.label}>
            <input
                type="number"
                className={props.narrow ? 'input-narrow' : undefined}
                value={Number.isFinite(props.value) ? props.value : 0}
                step={props.step ?? 1}
                onChange={(e) => props.onChange(Number(e.target.value))}
            />
        </Field>
    );
}

export function SelectField<T extends string>(props: {
    label: string; value: T; options: { value: T; label: string }[];
    onChange: (v: T) => void; placeholder?: string;
}) {
    return (
        <Field label={props.label}>
            <select value={props.value} onChange={(e) => props.onChange(e.target.value as T)}>
                {props.placeholder !== undefined && <option value="">{props.placeholder}</option>}
                {props.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </Field>
    );
}

export function Badge({ children, tone = 'neutral' }: {
    children: ReactNode; tone?: 'neutral' | 'coral' | 'teal' | 'error';
}) {
    return <span className={`badge ${tone === 'neutral' ? '' : tone}`}>{children}</span>;
}

export function Card({ children, tight }: { children: ReactNode; tight?: boolean }) {
    return <div className={`card${tight ? ' card-tight' : ''}`}>{children}</div>;
}

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
    return (
        <div className="empty">
            <h3>{title}</h3>
            {hint && <p className="small" style={{ marginTop: 6 }}>{hint}</p>}
            {action && <div style={{ marginTop: 14 }}>{action}</div>}
        </div>
    );
}

export function Banner({ tone, title, items, children }: {
    tone: 'error' | 'ok' | 'neutral'; title: string; items?: string[]; children?: ReactNode;
}) {
    return (
        <div className={`banner ${tone === 'neutral' ? '' : tone}`}>
            <strong>{title}</strong>
            {items && items.length > 0 && (
                <ul>{items.map((item, i) => <li key={i}>{item}</li>)}</ul>
            )}
            {children}
        </div>
    );
}

/** Async state wrapper so every screen handles loading and failure the same way. */
export function Async<T>({ state, children }: {
    state: { loading: boolean; error: string | null; data: T | null };
    children: (data: T) => ReactNode;
}) {
    if (state.loading) return <div className="spinner">Loading…</div>;
    if (state.error) return <Banner tone="error" title="Could not load" items={[state.error]} />;
    if (state.data === null) return null;
    return <>{children(state.data)}</>;
}

/**
 * Editable list of free-text tags. Kept deliberately plain — tags are an open vocabulary
 * by design, so there is nothing to constrain them to.
 */
export function TagsField({ label, value, onChange }: {
    label: string; value: string[]; onChange: (v: string[]) => void;
}) {
    const [draft, setDraft] = useState('');
    return (
        <Field label={label}>
            <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                {value.map((tag) => (
                    <span key={tag} className="badge">
                        {tag}
                        <button
                            className="btn icon secondary"
                            style={{ padding: '0 4px', border: 'none', background: 'transparent' }}
                            onClick={() => onChange(value.filter((t) => t !== tag))}
                            aria-label={`Remove ${tag}`}
                        >×</button>
                    </span>
                ))}
                <input
                    style={{ width: 130 }}
                    value={draft}
                    placeholder="add tag…"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && draft.trim()) {
                            e.preventDefault();
                            const next = draft.trim().toLowerCase().replace(/[\s_]+/g, '-');
                            if (!value.includes(next)) onChange([...value, next]);
                            setDraft('');
                        }
                    }}
                />
            </div>
        </Field>
    );
}

/** Confirm destructive actions. Deletes here can cascade, so they get a real gate. */
export function useConfirm() {
    return (message: string) => window.confirm(message);
}

/** Debounced autosave indicator. */
export function SaveState({ saving, savedAt }: { saving: boolean; savedAt: Date | null }) {
    const [, tick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => tick((n) => n + 1), 10000);
        return () => clearInterval(id);
    }, []);

    if (saving) return <span className="small muted">Saving…</span>;
    if (!savedAt) return null;
    return <span className="small muted">Saved</span>;
}
